import { MIPCreateContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/MIPCreateContext';
import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { completeMipCreation } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	useTranslationDefault,
	useTranslationMipCreate,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { ERROR_CODES } from '@dealstack/shared/lib/common/errors';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { some } from 'lodash';
import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { ProgrammeStatus } from '@dealstack/shared/lib/generated';
import { SHOW_MIP_MANAGE_CONGRATULATION_MODAL } from '../../MIPManage/constants';

export const useDealSummaryController = () => {
	const completeMipCreationRequest = useWrappedRequest(completeMipCreation);
	const mipCreateController = useContext(MIPCreateContext);
	const tMipCreate = useTranslationMipCreate();
	const tDefault = useTranslationDefault();
	const navigate = useNavigate();

	const sortedStepStatuses = useMemo(
		() => mipCreateController?.stepsStatuses?.sort((a, b) => b.status.length - a.status.length),
		[mipCreateController?.stepsStatuses]
	);

	const areStepErrors = useMemo(() => {
		const result = some(
			mipCreateController?.stepsStatuses?.map((item) => item.status),
			(item) => ['ERROR_MISSING_DATA', 'ERROR_INVALID_DATA'].includes(item)
		);
		return result;
	}, [mipCreateController?.stepsStatuses]);

	const isLoading =
		completeMipCreationRequest.isLoading ||
		mipCreateController?.getMipDraftStepsStatusesRequest?.isLoading;

	const isSubmitBlocked = isLoading || areStepErrors;
	const dealId = mipCreateController?.dealId ?? '';
	const mipId = mipCreateController?.mipId ?? '';

	const onComplete = () => {
		if (isSubmitBlocked) {
			return;
		}

		const investorsPath = mipManagePath(MIP_MANAGE_ROUTES.INVESTORS, { dealId, mipId });
		// Land on the investors tab with the Joiners quick-filter pre-applied.
		const mipManageUrl = `${investorsPath}?quickFilters=${encodeURIComponent(
			JSON.stringify({ programmeStatuses: ProgrammeStatus.JOINER })
		)}`;

		completeMipCreationRequest
			.fetch()
			.onSuccess(() => {
				sessionStorage.setItem(
					formatUnicorn(SHOW_MIP_MANAGE_CONGRATULATION_MODAL, {
						mipId,
					}),
					'true'
				);
				showToastV2({
					title: tMipCreate('completion.success.title'),
					message: tMipCreate('completion.success.message'),
				});

				navigate(mipManageUrl);
			})
			.onLocalError((responseError) => {
				const errorMessage = (() => {
					if (
						isStandardBackendError(responseError) &&
						responseError?.error?.code ===
							ERROR_CODES.PROJECT.MIP.CREATE.COMPLETION[11010901]
					) {
						showToastV2({
							title: tDefault(`error_codes.${responseError.error.code}`),
							state: 'warning',
						});

						navigate(mipManageUrl);
						return;
					}

					if (isStandardBackendError(responseError)) {
						return tDefault(`error_codes.${responseError.error?.code}`);
					}
					return tMipCreate('completion.error.message');
				})();

				showToastV2({
					title: errorMessage,
					state: 'error',
				});
			});
	};

	return {
		completeMipCreationRequest,
		onComplete,
		areStepErrors,
		stepsStatuses: sortedStepStatuses,
		isLoading,
		isSubmitBlocked,
	};
};
